import React, { useState, useEffect } from 'react';
import {
  getPropelOrgs,
  createPropelOrg,
  updatePropelOrg,
  deactivatePropelOrg,
  activatePropelOrg,
  setDefaultPropelOrg
} from '../../services/adminApiService';
import PropelOrgForm from './PropelOrgForm';

const PropelOrgManagement = () => {
  const [propelOrgs, setPropelOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadPropelOrgs();
  }, [includeInactive]);

  async function loadPropelOrgs() {
    try {
      setLoading(true);
      setError(null);
      const response = await getPropelOrgs(includeInactive);
      setPropelOrgs(response.propelOrgs || []);
    } catch (err) {
      console.error('Error loading Propel orgs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setEditingOrg(null);
    setShowForm(true);
  }

  function handleEdit(org) {
    setEditingOrg(org);
    setShowForm(true);
  }

  function handleCancelForm() {
    setShowForm(false);
    setEditingOrg(null);
  }

  async function handleSubmitForm(data) {
    try {
      setError(null);
      setSuccessMessage('');

      if (editingOrg) {
        await updatePropelOrg(editingOrg.id, data);
        setSuccessMessage('Propel organization updated successfully!');
      } else {
        await createPropelOrg(data);
        setSuccessMessage('Propel organization created successfully!');
      }

      setShowForm(false);
      setEditingOrg(null);
      loadPropelOrgs();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error submitting Propel org:', err);
      setError(err.message);
    }
  }

  async function handleSetDefault(id) {
    try {
      setError(null);
      await setDefaultPropelOrg(id);
      setSuccessMessage('Default Propel organization updated!');
      loadPropelOrgs();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error setting default Propel org:', err);
      setError(err.message);
    }
  }

  async function handleDeactivate(id) {
    if (!window.confirm('Are you sure you want to deactivate this Propel organization?')) {
      return;
    }

    try {
      setError(null);
      await deactivatePropelOrg(id);
      setSuccessMessage('Propel organization deactivated successfully!');
      loadPropelOrgs();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deactivating Propel org:', err);
      setError(err.message);
    }
  }

  async function handleActivate(id) {
    try {
      setError(null);
      await activatePropelOrg(id);
      setSuccessMessage('Propel organization activated successfully!');
      loadPropelOrgs();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error activating Propel org:', err);
      setError(err.message);
    }
  }

  if (loading) {
    return <div className="admin-loading">Loading Propel organizations...</div>;
  }

  return (
    <div className="propel-org-management">
      <div className="management-header">
        <h2>Propel Organization Management</h2>
        <div className="management-actions">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show Inactive
          </label>
          <button onClick={handleCreate} className="btn btn-primary">
            Add New Propel Org
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content">
            <PropelOrgForm
              propelOrg={editingOrg}
              onSubmit={handleSubmitForm}
              onCancel={handleCancelForm}
            />
          </div>
        </div>
      )}

      <div className="propel-org-list">
        {propelOrgs.length === 0 ? (
          <div className="empty-state">
            No Propel organizations found. Click "Add New Propel Org" to create one.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Label</th>
                <th>Tenant</th>
                <th>Instance URL</th>
                <th>Client ID</th>
                <th>Default</th>
                <th>Status</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {propelOrgs.map((org) => (
                <tr key={org.id} className={!org.is_active ? 'inactive-row' : ''}>
                  <td>{org.id}</td>
                  <td><strong>{org.org_label}</strong></td>
                  <td>{org.company_name || 'Unknown'}</td>
                  <td><code className="small-code">{org.propel_instance_url || 'N/A'}</code></td>
                  <td><code className="small-code">{org.propel_client_id}</code></td>
                  <td>
                    {org.is_default ? (
                      <span className="badge badge-primary">Default</span>
                    ) : (
                      <button
                        onClick={() => handleSetDefault(org.id)}
                        className="btn btn-tiny btn-link"
                        disabled={!org.is_active}
                      >
                        Set Default
                      </button>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${org.is_active ? 'active' : 'inactive'}`}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {org.last_used_at
                      ? new Date(org.last_used_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(org)}
                        className="btn btn-small btn-secondary"
                        disabled={!org.is_active}
                      >
                        Edit
                      </button>
                      {org.is_active ? (
                        <button
                          onClick={() => handleDeactivate(org.id)}
                          className="btn btn-small btn-danger"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(org.id)}
                          className="btn btn-small btn-success"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PropelOrgManagement;
